// btw-front/src/lib/useQueryState.js
// 목록 화면의 필터·검색어·정렬을 URL 쿼리(?key=)에 보관하는 useState 대체 훅.
// 상세 화면에 갔다가 뒤로가기·'목록으로'로 돌아와도 같은 조건의 목록이 다시 그려져
// 스크롤 위치 복원(components/system/ScrollToTop)이 맞는 자리로 간다.
// - 값은 컴포넌트 상태로 들고 URL 에는 따라 쓰기만 한다. 입력창을 URL 에 직접 묶으면
//   라우터 갱신 타이밍 때문에 한글 조합(IME)이 끊길 수 있어서다.
// - 기본값이면 쿼리에서 지워 URL 을 깔끔하게 유지하고, 히스토리를 쌓지 않게 replace 로 쓴다.
// - 화면에 머문 채 바깥에서 URL 이 바뀌면(Navbar 홈 링크 PUSH, 뒤로·앞으로 POP) 상태도 URL 을 따른다.
import { useEffect, useRef, useState } from "react";
import { useNavigationType, useSearchParams } from "react-router-dom";

export function useQueryState(key, defaultValue = "") {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigationType = useNavigationType();
  const urlValue = searchParams.get(key) ?? defaultValue;
  const [value, setValue] = useState(urlValue);
  // setSearchParams 는 URL 이 바뀔 때마다 새로 만들어진다. 쓰기 효과의 의존성에 넣으면 바깥 URL 변경 직후
  // 아직 반영 전인 옛 값으로 한 번 더 쓰게 되므로 ref 로 최신 것만 참조한다.
  const setSearchParamsRef = useRef(setSearchParams);
  setSearchParamsRef.current = setSearchParams;

  // 바깥에서 바뀐 URL 반영. 이 훅이 쓰는 갱신은 모두 REPLACE 라 제외한다 — 빠르게 타이핑할 때
  // 한 박자 늦게 도착한 자기 갱신(이전 글자까지의 값)이 입력 중인 값을 덮지 않도록.
  useEffect(() => {
    if (navigationType !== "REPLACE") setValue(urlValue);
  }, [urlValue, navigationType]);

  useEffect(() => {
    const next = value === defaultValue ? null : value;
    if (searchParams.get(key) === next) return;
    setSearchParamsRef.current(
      (prev) => {
        const params = new URLSearchParams(prev);
        if (next === null) params.delete(key);
        else params.set(key, next);
        return params;
      },
      { replace: true },
    );
    // 값이 바뀔 때만 쓴다. searchParams 는 비교용으로만 읽어 의존성에서 뺀다
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, value, defaultValue]);

  return [value, setValue];
}

// 정렬 상태 { key, dir } ↔ 쿼리 문자열 "key.dir" 변환. 형식이 틀리면 정렬 없음(null).
export function parseSort(raw) {
  const m = /^([A-Za-z]+)\.(asc|desc)$/.exec(raw || "");
  return m ? { key: m[1], dir: m[2] } : null;
}

export function formatSort(sort) {
  return sort ? `${sort.key}.${sort.dir}` : "";
}
